import { useState } from "react";
import { History, RotateCcw } from "lucide-react";
import {
  getAdminTradovateBackfillRepair,
  rerunTradovateBackfill,
  useQuery,
} from "wasp/client/operations";
import { Card, CardContent } from "../../../client/components/ui/card";
import { Button } from "../../../client/components/ui/button";
import useTranslation from "../../../client/hooks/useTranslation";

/**
 * Réparation du backfill (incident du 2026-10-19) : comptes dont les trades
 * backfillés ne comptent AUCUN perdant, signature du parseur d'avant le
 * correctif. « Rejouer » relance le job sur le compte ; le parseur corrigé
 * écrit les perdants manquants et, pour un compte importé, remet le solde
 * initial d'aplomb.
 */
const TradovateBackfillRepairCard = () => {
  const t = useTranslation();
  const { data, refetch } = useQuery(getAdminTradovateBackfillRepair);
  const [requested, setRequested] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const rows = (data?.rows ?? []) as Array<{
    syncedAccountId: string;
    accountName: string;
    userEmail: string | null;
    isActive: boolean;
    initialBalance: number;
    brokerBalance: number | null;
    backfilledTrades: number;
    note: string | null;
    plan: string;
  }>;
  const activeIds = rows
    .filter((r) => r.isActive && r.plan !== "none")
    .map((r) => r.syncedAccountId);

  const replay = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBusy(true);
    setError(false);
    try {
      await rerunTradovateBackfill({ syncedAccountIds: ids });
      setRequested((prev) => new Set([...prev, ...ids]));
      void refetch();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = (plan: string): string => {
    switch (plan) {
      case "new_account":
        return t.adminDashboard.backfillRepairKindNew;
      case "gap":
        return t.adminDashboard.backfillRepairKindGap;
      case "new_account+gap":
        return t.adminDashboard.backfillRepairKindBoth;
      default:
        return t.adminDashboard.backfillRepairKindNone;
    }
  };
  // La note du job s'enrichit à chaque passage (« | ») : seule la dernière
  // entrée dit où en est le compte.
  const lastRun = (note: string | null) => (note ? note.split(" | ").pop() ?? "" : "");

  const money = (n: number | null) =>
    n == null ? "-" : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <History className="h-4 w-4" />
        {t.adminDashboard.backfillRepairTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">{t.adminDashboard.backfillRepairHint}</p>
      <Card>
        <CardContent className="pt-4">
          {rows.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {t.adminDashboard.backfillRepairEmpty}
            </p>
          ) : (
            <>
              {error && (
                <p className="text-pnlNegative mb-3 text-xs">{t.adminDashboard.backfillRepairError}</p>
              )}
              <div className="mb-3 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || activeIds.every((id) => requested.has(id))}
                  onClick={() => replay(activeIds.filter((id) => !requested.has(id)))}
                >
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                  {t.adminDashboard.backfillRepairReplayAll}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-left text-xs">
                      <th className="pb-2 font-normal">{t.adminDashboard.backfillRepairColAccount}</th>
                      <th className="pb-2 font-normal">{t.adminDashboard.backfillRepairColUser}</th>
                      <th className="pb-2 text-right font-normal">{t.adminDashboard.backfillRepairColTrades}</th>
                      <th className="pb-2 text-right font-normal">{t.adminDashboard.backfillRepairColInitial}</th>
                      <th className="pb-2 text-right font-normal">{t.adminDashboard.backfillRepairColBroker}</th>
                      <th className="pb-2 pl-4 font-normal">{t.adminDashboard.backfillRepairColKind}</th>
                      <th className="pb-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.syncedAccountId} className="border-border/50 border-b">
                        <td className="text-foreground py-2 font-mono text-xs">
                          {r.accountName}
                          {r.note && (
                            <span
                              className="text-muted-foreground block max-w-[260px] truncate font-sans text-[10px]"
                              title={r.note}
                            >
                              {t.adminDashboard.backfillRepairLastRun} : {lastRun(r.note)}
                            </span>
                          )}
                        </td>
                        <td className="text-muted-foreground py-2 text-xs">{r.userEmail ?? "-"}</td>
                        <td className="py-2 text-right font-mono text-xs">{Number(r.backfilledTrades)}</td>
                        <td className="py-2 text-right font-mono text-xs">{money(Number(r.initialBalance))}</td>
                        <td className="py-2 text-right font-mono text-xs">
                          {money(r.brokerBalance == null ? null : Number(r.brokerBalance))}
                        </td>
                        <td className="text-muted-foreground py-2 pl-4 text-xs">
                          {!r.isActive ? t.adminDashboard.backfillRepairInactive : kindLabel(r.plan)}
                        </td>
                        <td className="py-2 text-right">
                          {r.isActive && r.plan !== "none" &&
                            (requested.has(r.syncedAccountId) ? (
                              <span className="text-muted-foreground text-xs">
                                {t.adminDashboard.backfillRepairRequested}
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busy}
                                onClick={() => replay([r.syncedAccountId])}
                              >
                                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                                {t.adminDashboard.backfillRepairReplay}
                              </Button>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateBackfillRepairCard;
